import { Component, OnInit, OnDestroy } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { Utils } from 'src/app/utils/utilities';
import {
  ZipYiService,
  ThanksScriptService,
  CheckoutV2Service,
  GiftCardsV2Service,
  TrxService,
} from '~services';
import moment from 'moment';
import { ZipYiContext } from '~services/biocatch/zip-yi-context.enum';
import { Subject } from 'rxjs';
import { CreateOrderRequest, Order } from '~models/order.model';
import { OrderStatus } from '~models/checkout.model';
import { Section } from '~services/trx/events';
import { CURRENT_ENTRY_ID_SESSION_KEY } from 'src/constants';

@Component({
  selector: 'app-complete-view-v2',
  templateUrl: './complete.component.html',
  styleUrls: ['./complete.component.scss'],
  standalone: false,
})
export class CompleteViewV2Component implements OnInit, OnDestroy {
  public isLoading = true;
  public deliveryDateUI?: string;
  public order: Order;

  private pollingInterval: number;
  private pollingTimeout: number;
  private productId?: string;
  private orderId?: string;
  private checkoutId?: string;
  private isCheckoutSuccessful: boolean = false;
  private isCheckoutCancelled: boolean = false;

  private destroy$ = new Subject<boolean>();

  constructor(
    private activatedRoute: ActivatedRoute,
    private ngRouter: Router,
    private checkoutV2Service: CheckoutV2Service,
    private giftCardV2Service: GiftCardsV2Service,
    public readonly location: Location,
    private zipYiService: ZipYiService,
    private thanksService: ThanksScriptService,
    private trxService: TrxService,
  ) {
    this.productId = this.activatedRoute.snapshot.queryParams.productId;
    this.orderId = this.activatedRoute.snapshot.queryParams.orderId;
    this.checkoutId = this.activatedRoute.snapshot.queryParams.checkoutId;
    this.isCheckoutSuccessful = this.activatedRoute.snapshot.queryParams.result === 'approved';
    this.isCheckoutCancelled = this.activatedRoute.snapshot.queryParams.result === 'cancelled';
  }

  async ngOnInit() {
    if (!this.checkoutId) {
      return this.ngRouter.navigateByUrl('/');
    }

    if (this.isCheckoutCancelled) {
      return this.navigateToCheckout();
    }

    this.zipYiService.setContext(ZipYiContext.PurchaseCompletePage);
    this.trxService.sectionStartEvent(Section.GiftcardPurchaseSucess);
    await this.startProcessingOrder();
  }

  ngOnDestroy(): void {
    this.stopPolling();
    this.destroy$.next(true);
  }

  private async startProcessingOrder() {
    try {
      if (!this.isCheckoutSuccessful) {
        throw new Error('Checkout is not successful');
      }

      const createOrderRequest: CreateOrderRequest = {
        orderId: this.orderId,
        checkoutId: this.checkoutId,
      };

      if (!this.isOrderCreated) {
        this.order = await this.checkoutV2Service.createOrder(createOrderRequest);
        this.orderId = this.order.id;
        const queryParams = {
          ...this.activatedRoute.snapshot.queryParams,
          isOrderCreated: true,
        };
        this.ngRouter.navigate(['/finalise'], { replaceUrl: true, queryParams });
      } else {
        await this.checkOrderStatus();
      }

      if (Utils.checkValid(this.order.gifting?.deliveryDate)) {
        this.deliveryDateUI = moment(this.order.gifting.deliveryDate).format('DD/MM/YYYY');
      }

      if (this.isOrderPending) {
        this.startPolling();
      }
    } catch (error) {
      window.zbm?.reportError(error);
      this.trxService.sectionFailedEvent();
      this.trxService.funnelFailedEvent();
      this.ngRouter.navigate(['/error', "Uh oh, something's not right."]);
    }
  }

  private async getGiftCardCategories() {
    if (this.entryId) {
      const categories = await this.giftCardV2Service.getCategoriesByEntryId(this.entryId);
      return categories.map((category) => category.name);
    }

    return [];
  }

  private async checkOrderStatus() {
    this.order = await this.checkoutV2Service.getOrderById(this.orderId);

    if (this.simulatePending) {
      this.order.status = OrderStatus.PENDING;
    }

    switch (this.order.status) {
      case OrderStatus.FAILED: {
        this.stopPolling();
        this.isLoading = false;
        const error = new Error(`Received invalid order zipStatus: '${this.order.status}'`);
        window.zbm?.reportError(error);
        this.ngRouter.navigate(['/error', `Uh oh, something's not right.`]);
        break;
      }
      case OrderStatus.SUCCESS:
        this.onOrderComplete();
        break;
      default:
        break;
    }
  }

  private onOrderComplete() {
    this.stopPolling();
    this.isLoading = false;
    this.trxService.funnelSuccessEvent('purchased gift card');
    this.addThanksScript();
  }

  private timeout() {
    this.isLoading = false;
    this.stopPolling();
    this.trxService.funnelSuccessEvent('purchased gift card');
    this.addThanksScript();
  }

  private startPolling() {
    this.pollingInterval = setInterval(() => this.checkOrderStatus(), 2000);
    this.pollingTimeout = setTimeout(() => this.timeout(), 10000);
  }

  private stopPolling() {
    clearInterval(this.pollingInterval);
    clearTimeout(this.pollingTimeout);
  }

  private async addThanksScript() {
    this.thanksService.addThanksScript({
      email: this.order.email,
      giftCardName: this.order.product.name,
      giftCardValue: this.order.amount.toString(),
      categories: await this.getGiftCardCategories(),
    });
  }

  public navigateHome() {
    this.trxService.sectionSuccessEvent('continue shopping');
    this.ngRouter.navigateByUrl('/');
  }

  private navigateToCheckout() {
    this.ngRouter.navigateByUrl(`/checkout/${this.productId}`);
  }

  public get isOrderCreated() {
    return this.activatedRoute.snapshot.queryParams.isOrderCreated === 'true';
  }

  public get isOrderPending() {
    return [OrderStatus.PENDING, OrderStatus.FAILED_WITH_RETRY].includes(this.order.status);
  }

  public get isOrderCompleted() {
    return this.order.status === OrderStatus.SUCCESS;
  }

  public get simulatePending() {
    return this.activatedRoute.snapshot.queryParams.simulatePending === 'true';
  }

  private get entryId() {
    return sessionStorage.getItem(CURRENT_ENTRY_ID_SESSION_KEY);
  }
}
